import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL_NEW } from '@utils/api/endpoints.ts';
import { BaseError, BaseResponse } from '@utils/types/shared/base.ts';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params.ts';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

export type Team = {
  id: number;
  name: string;
  description: string;
};

export type TeamsResponse = BaseResponse<Team[]>;

export const useGetTeams = () => {
  const { t } = useTranslation();
  const { searchParams } = useNewAppSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;

  const sortField = (searchParams.sort as string | undefined) || undefined;
  const sortDirection =
    (searchParams.direction as 'asc' | 'desc' | undefined) || undefined;
  const qString = (searchParams.q as string | undefined) || undefined;

  const fetchTeams = async () => {
    const { teams: teamsUrl } = GET_URL_NEW({
      offset,
      limit,
      sort: sortField,
      direction: sortDirection,
      q: qString
    });
    const { data } = await Axios.get<TeamsResponse>(teamsUrl);

    return data;
  };

  return useQuery<TeamsResponse, AxiosError<BaseError>>(
    ['teams', offset, limit, sortField, sortDirection, qString],
    fetchTeams,
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,

      keepPreviousData: true,

      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,

      onError: error => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const d = error.response?.data as any;
        const msg =
          d?.error_description ||
          d?.detail ||
          d?.error ||
          d?.message ||
          t('an-error-has-occurred');
        toast.error(String(msg));
      }
    }
  );
};
